import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  applyLessonFix,
  discardLessonFix,
  listLessonFixes,
  requestLessonFix,
  revertLessonFix,
} from '@platform/client';
import { lessonFixesSchema } from '@platform/schema/lesson-fix';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';
import { reviewQuery } from '../library/sources';

import { ChangeTable } from './ChangeTable';

import type { RequestFixDto } from '@platform/client';
import type { Change } from '@platform/schema/changes';
import type {
  LessonFix,
  LessonFixStatus,
  Recheck,
} from '@platform/schema/lesson-fix';

/**
 * Fix with AI for a finding the scene editor cannot answer, on CURR-36's lesson fixes:
 * proposal, Apply, the re-check's result, Revert — CURR-38.
 */

/** Under `review`, so the workspace's refresh after any write reads it again. */
const fixesQuery = (lessonId: string) => ({
  queryKey: ['review', lessonId, 'fixes'],
  queryFn: async () =>
    lessonFixesSchema.parse(
      (await listLessonFixes({ path: { id: lessonId }, throwOnError: true }))
        .data,
    ).fixes,
});

export const useLessonFixes = (lessonId: string): readonly LessonFix[] =>
  useQuery(fixesQuery(lessonId)).data ?? [];

/** The newest fix asked for a stored finding; the list is newest first. */
export const fixFor = (
  fixes: readonly LessonFix[],
  findingId: string,
): LessonFix | undefined =>
  fixes.find((fix) => fix.finding.findingId === findingId);

/** The newest fix asked for one claim against one source. */
export const sourceFixFor = (
  fixes: readonly LessonFix[],
  url: string,
  claim: string,
): LessonFix | undefined =>
  fixes.find(
    (fix) =>
      fix.finding.kind === 'SOURCE' &&
      fix.finding.url === url &&
      fix.finding.claim === claim,
  );

export const STATUS_LABEL: Record<LessonFixStatus, string> = {
  PROPOSED: 'Fix proposed',
  FAILED: 'Fix failed',
  RESOLVED: 'Resolved',
  UNRESOLVED: 'Applied, still open',
  REVERTED: 'Open, fix reverted',
  DISCARDED: 'Open',
};

const RECHECK: Record<Recheck, string> = {
  REVIEWER: 'the reviewer',
  CLAIMS: 'the claim check',
  LABELS: 'the label check',
  RUBRIC: 'the rubric',
  RULES: 'the publish rules',
  SOURCES: 'Find / verify source',
};

const checks = (fix: LessonFix): string =>
  (fix.rechecked ?? []).map((one) => RECHECK[one]).join(' and ') || 'the check';

/** Every change a fix makes, in a reviewer's words, scene by scene. */
function fixChanges(fix: LessonFix): Change[] {
  const scene = fix.scenes.flatMap((one): Change[] => {
    const at = `Scene ${String(one.position)}`;
    if (one.change === 'ADDED')
      return [{ field: at, before: '', after: 'New scene' }];
    const moved =
      one.from !== undefined && one.from !== one.position
        ? [
            {
              field: at,
              before: `Scene ${String(one.from)}`,
              after: `Moved to scene ${String(one.position)}`,
            },
          ]
        : [];
    const source =
      one.source === undefined
        ? []
        : [
            {
              field: `${at}: source`,
              before: one.source.before ?? 'None',
              after: one.source.after ?? 'None',
            },
          ];
    const fields = (one.changes ?? []).map((change) => ({
      ...change,
      field: `${at}: ${change.field}`,
    }));
    return [...moved, ...fields, ...source];
  });

  return [
    ...(fix.title === undefined ? [] : [{ field: 'Title', ...fix.title }]),
    ...(fix.summary === undefined
      ? []
      : [{ field: 'Summary', ...fix.summary }]),
    ...scene,
    ...fix.removed.map((position) => ({
      field: `Scene ${String(position)}`,
      before: 'In the lesson',
      after: 'Cut',
    })),
    ...fix.sources.added.map((one) => ({
      field: 'Sources',
      before: '',
      after: `${one.title} (${one.url})`,
    })),
    ...fix.sources.removed.map((one) => ({
      field: 'Sources',
      before: `${one.title} (${one.url})`,
      after: '',
    })),
    ...(fix.claim === undefined || fix.claim === fix.finding.claim
      ? []
      : [
          {
            field: 'Claim',
            before: fix.finding.claim ?? '',
            after: fix.claim ?? 'Cut from the lesson',
          },
        ]),
  ];
}

function Remaining({ fix }: { fix: LessonFix }) {
  return (
    <>
      {fix.remaining.map((line) => (
        <Typography key={line} variant="body2">
          {line}
        </Typography>
      ))}
      {fix.nextStep !== undefined && (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          Next step: {fix.nextStep}
        </Typography>
      )}
    </>
  );
}

type Verb = 'apply' | 'discard' | 'revert';

/** What can be done to a fix in each status; anything else can be asked for again. */
const VERBS: Partial<Record<LessonFixStatus, readonly Verb[]>> = {
  PROPOSED: ['apply', 'discard'],
  RESOLVED: ['revert'],
  UNRESOLVED: ['revert'],
};

const TITLE: Record<Verb, string> = {
  apply: 'Apply',
  discard: 'Discard',
  revert: 'Revert',
};

function Said({ fix }: { fix: LessonFix }) {
  switch (fix.status) {
    case 'PROPOSED':
      return (
        <Alert severity="warning">
          Fix with AI proposes the change below. Nothing changes until you apply
          it.
          {fix.why !== undefined && (
            <Typography variant="body2">Why: {fix.why}</Typography>
          )}
        </Alert>
      );
    case 'RESOLVED':
      return (
        <Alert severity="success">
          Applied. Re-checked by {checks(fix)}: the finding is resolved.
        </Alert>
      );
    case 'UNRESOLVED':
      return (
        <Alert severity="warning">
          Applied, but the re-check by {checks(fix)} still finds it. The fix
          stays applied and the finding stays open.
          <Remaining fix={fix} />
        </Alert>
      );
    case 'FAILED':
      return (
        <Alert severity="error">
          Fix with AI could not propose a fix. Nothing changed.
          <Remaining fix={fix} />
        </Alert>
      );
    case 'REVERTED':
      return (
        <Alert severity="info">
          You reverted this fix. The lesson is back as it was, and the finding
          is open again.
        </Alert>
      );
    case 'DISCARDED':
      return null;
  }
}

/**
 * One finding's fix, from the request to Revert. `fix` is the newest one asked for it;
 * `request` is what to ask with.
 */
export function LessonFixFlow({
  lessonId,
  fix,
  request,
}: {
  lessonId: string;
  fix: LessonFix | undefined;
  request: RequestFixDto;
}) {
  const queries = useQueryClient();
  const refresh = () =>
    queries.invalidateQueries({ queryKey: reviewQuery(lessonId).queryKey });
  const published =
    useQuery({ ...reviewQuery(lessonId), select: (one) => one.status }).data ===
    'PUBLISHED';

  const ask = useTrackedMutation('Fix with AI', {
    mutationFn: async () => {
      await requestLessonFix({
        path: { id: lessonId },
        body: request,
        throwOnError: true,
      });
    },
    onSuccess: refresh,
  });
  const decide = useTrackedMutation('Decide on the fix', {
    mutationFn: async ({ verb, id }: { verb: Verb; id: string }) => {
      const call = {
        apply: applyLessonFix,
        discard: discardLessonFix,
        revert: revertLessonFix,
      }[verb];
      await call({ path: { id }, throwOnError: true });
    },
    onSuccess: refresh,
  });

  const busy = ask.isPending || decide.isPending;
  const failure = ask.error ?? decide.error;
  const button = (text: string, onClick: () => void, primary = false) => (
    <Button
      key={text}
      size="small"
      variant={primary ? 'contained' : 'outlined'}
      disabled={busy}
      onClick={onClick}
    >
      {text}
    </Button>
  );
  const verbs = fix === undefined ? undefined : VERBS[fix.status];
  const actions =
    fix !== undefined && verbs !== undefined
      ? verbs.map((verb) =>
          button(
            TITLE[verb],
            () => {
              decide.mutate({ verb, id: fix.id });
            },
            verb === 'apply',
          ),
        )
      : [
          button(fix?.status === 'FAILED' ? 'Try again' : 'Fix with AI', () => {
            ask.mutate();
          }),
        ];
  // A discarded proposal was never written, so its diff would read as the lesson's.
  const changes =
    fix === undefined || fix.status === 'FAILED' || fix.status === 'DISCARDED'
      ? []
      : fixChanges(fix);

  return (
    <Stack spacing={1} data-testid="lesson-fix">
      {fix !== undefined && <Said fix={fix} />}
      {changes.length > 0 && <ChangeTable changes={changes} />}
      {ask.isPending && (
        <Stack spacing={0.5}>
          <Typography variant="body2">
            Asking the editor for a fix. This takes about a minute.
          </Typography>
          <LinearProgress />
        </Stack>
      )}
      {decide.isPending && <LinearProgress />}
      {failure !== null && (
        <Alert severity="error">{describeApiError(failure)}</Alert>
      )}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        {actions}
        <Typography variant="caption" color="text.secondary">
          This lesson
        </Typography>
      </Stack>
      {/* A fix writes the working copy; learners read the last publish — CURR-46. */}
      {published && (
        <Typography variant="caption" color="text.secondary">
          Published: learners keep the published version until the module is
          published.
        </Typography>
      )}
    </Stack>
  );
}
